import { useState, useEffect, useCallback, useMemo } from "react";
import { FolderTree, Plus, Pencil, Trash2, X, Eye, EyeOff, GripVertical } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { AdminSelect } from "./AdminSelect";
import ImageUploadField from "./ImageUploadField";

const EMPTY_FORM = { name: "", slug: "", description: "", parent: "", sort_order: 0, image_url: "", visible: true };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/api/admin/categories");
      setCategories(res.data.categories || []);
    } catch (err) { console.error("[AdminCategories] fetch failed:", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY_FORM }); setShowForm(true); };
  const openEdit = (cat) => { setEditing(cat.id); setForm({ name: cat.name || "", slug: cat.slug || "", description: cat.description || "", parent: cat.parent || "", sort_order: cat.sort_order || 0, image_url: cat.image_url || "", visible: cat.visible !== false }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const selectableCategories = useMemo(
    () => categories.filter((c) => c.id !== editing),
    [categories, editing]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await adminApi.put(`/api/admin/categories/${editing}`, form);
      } else {
        await adminApi.post("/api/admin/categories", form);
      }
      closeForm();
      fetchCategories();
    } catch (err) { console.error("[AdminCategories] save failed:", err); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await adminApi.delete(`/api/admin/categories/${id}`);
      fetchCategories();
    } catch (err) { console.error("[AdminCategories] delete failed:", err); }
  };

  const toggleVisibility = async (cat) => {
    try {
      await adminApi.put(`/api/admin/categories/${cat.id}`, { ...cat, visible: !cat.visible });
      fetchCategories();
    } catch (err) { console.error("[AdminCategories] toggle visibility failed:", err); }
  };

  const updateField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="admin-categories">
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
        <FolderTree size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Category Manager</h3>
        <span className="text-[10px] text-white/30">{categories.length} categories</span>
        <button onClick={openNew} className="ml-auto flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90 transition-opacity" data-testid="categories-add-btn">
          <Plus size={12} /> Add Category
        </button>
      </div>

      <div className="p-5">
        {/* Form */}
        {showForm && (
          <div className="border border-[#CBA052]/30 bg-[#0a0a0a] rounded p-5 mb-5" data-testid="category-form">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xs font-bold text-[#CBA052] uppercase tracking-wider">{editing ? "Edit Category" : "New Category"}</h4>
              <button onClick={closeForm} className="text-white/30 hover:text-white"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Name</span>
                <input value={form.name} onChange={(e) => updateField("name", e.target.value)} className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" data-testid="category-name-input" />
              </label>
              <label className="block">
                <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Slug</span>
                <input value={form.slug} onChange={(e) => updateField("slug", e.target.value)} placeholder="auto-generated" className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" />
              </label>
              <label className="block">
                <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Parent Category</span>
                <AdminSelect value={form.parent} onChange={(e) => updateField("parent", e.target.value)} size="md">
                  <option value="">None (top level)</option>
                  {selectableCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </AdminSelect>
              </label>
              <label className="block">
                <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Sort Order</span>
                <input value={form.sort_order} onChange={(e) => updateField("sort_order", parseInt(e.target.value, 10) || 0)} type="number" className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" />
              </label>
              <div className="block md:col-span-2">
                <ImageUploadField
                  value={form.image_url}
                  onChange={(url) => updateField("image_url", url)}
                  label="Category Image"
                  category="category"
                  group="Categories"
                  testIdPrefix="category-image"
                />
              </div>
              <label className="block md:col-span-2">
                <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Description</span>
                <textarea value={form.description} onChange={(e) => updateField("description", e.target.value)} rows={2} className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none resize-none" />
              </label>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
                <input type="checkbox" checked={form.visible} onChange={(e) => updateField("visible", e.target.checked)} className="accent-[#CBA052]" />
                Visible on storefront
              </label>
              <div className="ml-auto flex gap-2">
                <button onClick={closeForm} className="text-xs text-white/30 border border-white/10 px-4 py-2 rounded hover:text-white/60 transition-colors">Cancel</button>
                <button onClick={handleSave} disabled={saving || !form.name.trim()} className="text-xs font-bold text-black bg-[#CBA052] px-4 py-2 rounded hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="category-save-btn">
                  {saving ? "Saving..." : editing ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Category List */}
        {loading ? (
          <div className="text-xs text-white/30 text-center py-8">Loading categories...</div>
        ) : categories.length === 0 ? (
          <div className="text-xs text-white/30 text-center py-8" data-testid="categories-empty">No categories yet. Create your first category to organise products.</div>
        ) : (
          <div className="space-y-2" data-testid="categories-list">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-3 border border-white/[0.06] bg-white/[0.02] rounded p-3 group hover:border-white/10 transition-colors" data-testid={`category-row-${cat.id}`}>
                <GripVertical size={14} className="text-white/10 shrink-0 cursor-grab" />
                {cat.image_url ? (
                  <div className="w-10 h-10 rounded overflow-hidden shrink-0"><img src={cat.image_url} alt="" className="w-full h-full object-cover" /></div>
                ) : (
                  <div className="w-10 h-10 rounded bg-white/[0.03] flex items-center justify-center shrink-0"><FolderTree size={14} className="text-white/15" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-white truncate">{cat.name}</span>
                    {cat.parent && <span className="text-[9px] text-white/25">in {categories.find((c) => c.id === cat.parent)?.name || "..."}</span>}
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${cat.visible !== false ? "bg-green-500/15 text-green-400 border-green-500/20" : "bg-white/5 text-white/30 border-white/10"}`}>
                      {cat.visible !== false ? "Visible" : "Hidden"}
                    </span>
                  </div>
                  <p className="text-[10px] text-white/30 truncate">{cat.slug || "—"}{cat.description ? ` — ${cat.description}` : ""}</p>
                </div>
                <span className="text-[10px] text-white/20 shrink-0">#{cat.sort_order || 0}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button onClick={() => toggleVisibility(cat)} className="p-1.5 bg-white/5 rounded hover:bg-white/10" title={cat.visible !== false ? "Hide" : "Show"}>
                    {cat.visible !== false ? <Eye size={11} className="text-white/40" /> : <EyeOff size={11} className="text-white/40" />}
                  </button>
                  <button onClick={() => openEdit(cat)} className="p-1.5 bg-white/5 rounded hover:bg-white/10" title="Edit"><Pencil size={11} className="text-white/40" /></button>
                  <button onClick={() => handleDelete(cat.id)} className="p-1.5 bg-white/5 rounded hover:bg-red-500/20" title="Delete"><Trash2 size={11} className="text-white/40" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
